import React from "react";
import {
  Box,
  Chip,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import { SortType } from "../../../../../models/SortType";
import {
  AccountRow,
  AccountSortBy,
  CategoryNode,
} from "../../../../../models/Dashboard/AccountManagement/AccountManagement";
import { formatDateWithoutTime } from "../../../../../views/components/common/functions/formatDate";

interface AccountManagementTableProps {
  rows: AccountRow[];
  categories: CategoryNode[];
  page_no: number;
  limit: number;
  total_count: number;
  sortBy: AccountSortBy;
  sortType: SortType;
  onSortChange: (column: AccountSortBy, sortType: SortType) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange: (limit: number) => void;
  onQuarterClick?: (params: {
    customer_id: number;
    account_name: string;
    fy_year: number;
    quarter: number;
  }) => void;
}

type ColumnDef = {
  key: keyof AccountRow | "category";
  label: string;
  sortable?: AccountSortBy;
  align?: "left" | "center" | "right";
  isCurrency?: boolean;
  isDate?: boolean;
  isQuarter?: boolean;
  minWidth: string;
};

const parseQuarterKey = (
  key: string,
): { fy_year: number; quarter: number } | null => {
  const match = /^fy(\d{2})_q([1-4])$/.exec(key);
  if (!match) return null;
  return {
    fy_year: Number(match[1]),
    quarter: Number(match[2]),
  };
};

const buildQuarterColumns = (): ColumnDef[] => {
  const years = [21, 22, 23, 24, 25, 26];
  const quarters = [1, 2, 3, 4];
  const result: ColumnDef[] = [];

  years.forEach((year) => {
    quarters.forEach((quarter) => {
      const key = `fy${year}_q${quarter}` as keyof AccountRow;
      result.push({
        key,
        label: `FY${year} Q${quarter}`,
        align: "right",
        isCurrency: true,
        isQuarter: true,
        minWidth: "120px",
      });
    });
  });

  return result;
};

const QUARTER_COLUMNS = buildQuarterColumns();

const columns: ColumnDef[] = [
  {
    key: "account_name",
    label: "Account",
    sortable: "account_name",
    align: "left",
    minWidth: "200px",
  },
  {
    key: "effective_sales_poc",
    label: "Sales POC",
    align: "left",
    minWidth: "140px",
  },
  {
    key: "last_order_sales_poc",
    label: "Last Order POC",
    align: "left",
    minWidth: "140px",
  },
  { key: "category", label: "Category", align: "left", minWidth: "150px" },
  {
    key: "first_order_date",
    label: "First Order",
    sortable: "first_order_date",
    align: "center",
    isDate: true,
    minWidth: "120px",
  },
  {
    key: "last_order_date",
    label: "Last Order",
    sortable: "last_order_date",
    align: "center",
    isDate: true,
    minWidth: "120px",
  },
  {
    key: "lifetime_value",
    label: "LTV",
    sortable: "lifetime_value",
    align: "right",
    isCurrency: true,
    minWidth: "130px",
  },
  {
    key: "lifetime_orders",
    label: "Orders",
    sortable: "lifetime_orders",
    align: "right",
    minWidth: "90px",
  },
  {
    key: "avg_order_value",
    label: "AOV",
    sortable: "avg_order_value",
    align: "right",
    isCurrency: true,
    minWidth: "120px",
  },
  {
    key: "current_fy_revenue",
    label: "Current FY",
    sortable: "current_fy_revenue",
    align: "right",
    isCurrency: true,
    minWidth: "130px",
  },
  {
    key: "last_fy_revenue",
    label: "Last FY",
    sortable: "last_fy_revenue",
    align: "right",
    isCurrency: true,
    minWidth: "130px",
  },
  {
    key: "revenue_3yr",
    label: "3Y Revenue",
    sortable: "revenue_3yr",
    align: "right",
    isCurrency: true,
    minWidth: "130px",
  },
  {
    key: "revenue_5yr",
    label: "5Y Revenue",
    sortable: "revenue_5yr",
    align: "right",
    isCurrency: true,
    minWidth: "130px",
  },
  ...QUARTER_COLUMNS,
];

const STICKY_CELL_SX = {
  position: "sticky" as const,
  left: 0,
  zIndex: 3,
  // Opaque fallback; per-row bgcolor is applied below and must stay opaque
  backgroundColor: "#FFFFFF",
  boxShadow: "4px 0 8px -4px rgba(30, 42, 68, 0.18)",
};

const STICKY_HEADER_SX = {
  ...STICKY_CELL_SX,
  zIndex: 5,
  backgroundColor: "#EEF1F8",
};

const CELL_SX = {
  py: 0.9,
  px: 1.25,
  borderBottom: "1px solid #EEF1F6",
  whiteSpace: "nowrap" as const,
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } | null => {
  try {
    const cleaned = hex.replace("#", "").trim();
    const full =
      cleaned.length === 3
        ? cleaned
            .split("")
            .map((char) => `${char}${char}`)
            .join("")
        : cleaned.slice(0, 6);

    if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;

    return {
      r: Number.parseInt(full.slice(0, 2), 16),
      g: Number.parseInt(full.slice(2, 4), 16),
      b: Number.parseInt(full.slice(4, 6), 16),
    };
  } catch (error) {
    console.error(error);
    return null;
  }
};

/**
 * Soft category tint blended onto white (opaque).
 * Avoid alpha hex like `#RRGGBB14` — sticky cells would stay translucent
 * and scrolled column text would show through the Account column.
 */
const getLightCategoryColor = (color: string | null | undefined) => {
  if (!color) return "#FFFFFF";
  const rgb = hexToRgb(color);
  if (!rgb) return "#FFFFFF";

  const amount = 0.08;
  const mix = (channel: number) =>
    Math.round(255 * (1 - amount) + channel * amount);
  const toHex = (value: number) => value.toString(16).padStart(2, "0");

  return `#${toHex(mix(rgb.r))}${toHex(mix(rgb.g))}${toHex(mix(rgb.b))}`;
};

const formatCurrency = (value: number | string | null | undefined) => {
  try {
    if (value == null || value === "") return "-";
    const parsed = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(parsed)) return "-";
    return `₹${parsed.toLocaleString("en-IN", {
      maximumFractionDigits: 0,
    })}`;
  } catch (error) {
    console.error(error);
    return "-";
  }
};

const formatDateValue = (value: string | null) => {
  if (!value) return "-";
  const formatted = formatDateWithoutTime(value);
  return formatted || "-";
};

const buildCategoryMap = (categories: CategoryNode[]) => {
  const map = new Map<number, { name: string; color: string | null }>();
  const walk = (nodes: CategoryNode[]) => {
    nodes.forEach((node) => {
      map.set(node.id, { name: node.name, color: node.color ?? null });
      if (node.children?.length) {
        walk(node.children);
      }
    });
  };
  walk(categories);
  return map;
};

const AccountManagementTable: React.FC<AccountManagementTableProps> = ({
  rows,
  categories,
  page_no,
  limit,
  total_count,
  sortBy,
  sortType,
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
  onQuarterClick,
}) => {
  const categoryMap = buildCategoryMap(categories);

  const renderSortIcon = (property: string) => {
    if (sortBy === property) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const renderHeaderLabel = (column: ColumnDef) => {
    if (!column.sortable) {
      return column.label;
    }

    return (
      <TableSortLabel
        active={sortBy === column.sortable}
        direction={sortType === SortType.ASC ? "asc" : "desc"}
        onClick={() =>
          onSortChange(
            column.sortable!,
            sortBy === column.sortable && sortType === SortType.DESC
              ? SortType.ASC
              : SortType.DESC,
          )
        }
        IconComponent={() => <span>{renderSortIcon(column.sortable!)}</span>}
        sx={{
          color: "#1E2A44",
          "&.Mui-active": { color: "#6455ED" },
          "&:hover": { color: "#6455ED" },
          "& .MuiTableSortLabel-icon": { opacity: 1 },
        }}
      >
        {column.label}
      </TableSortLabel>
    );
  };

  const renderCellContent = (
    column: ColumnDef,
    row: AccountRow,
    category: { name: string; color: string | null } | null,
  ) => {
    try {
      if (column.key === "category") {
        if (!category) return "-";
        const accent = category.color || "#6455ED";
        return (
          <Chip
            label={category.name}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 600,
              bgcolor: `${accent}1A`,
              color: accent,
              border: `1px solid ${accent}55`,
            }}
          />
        );
      }

      if (column.key === "account_name") {
        return (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.2 }}>
            <Box component="span" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {row.account_name || "-"}
            </Box>
            <Box
              component="span"
              sx={{
                fontSize: "0.7rem",
                fontWeight: 500,
                color: "#5C6785",
                lineHeight: 1.2,
              }}
            >
              ID: {row.customer_id}
            </Box>
          </Box>
        );
      }

      if (column.key === "effective_sales_poc") {
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
            }}
          >
            <span>{row.effective_sales_poc || "-"}</span>
          </Box>
        );
      }

      if (column.key === "last_order_date" || column.isDate) {
        const value = row[column.key as keyof AccountRow];
        return formatDateValue(typeof value === "string" ? value : null);
      }

      if (column.key === "lifetime_orders") {
        return row.lifetime_orders ?? "-";
      }

      if (column.isCurrency) {
        const value = row[column.key as keyof AccountRow];
        const formatted = formatCurrency(
          typeof value === "number" || typeof value === "string" ? value : null,
        );

        if (column.isQuarter && onQuarterClick) {
          const quarterMeta = parseQuarterKey(String(column.key));
          if (quarterMeta) {
            return (
              <Box
                component="button"
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onQuarterClick({
                    customer_id: row.customer_id,
                    account_name: row.account_name,
                    fy_year: quarterMeta.fy_year,
                    quarter: quarterMeta.quarter,
                  });
                }}
                sx={{
                  all: "unset",
                  cursor: "pointer",
                  color: "inherit",
                  "&:hover": {
                    opacity: 0.75,
                  },
                }}
              >
                {formatted}
              </Box>
            );
          }
        }

        return formatted;
      }

      const value = row[column.key as keyof AccountRow];
      return value == null || value === "" ? "-" : String(value);
    } catch (error) {
      console.error(error);
      return "-";
    }
  };

  if (rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 180,
          border: "1px dashed #D8DEE8",
          borderRadius: 2,
          mt: 2,
          bgcolor: "#FBFCFF",
        }}
      >
        <Typography variant="body2" sx={{ color: "#5C6785" }}>
          No accounts found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          maxWidth: "100%",
          maxHeight: "70vh",
          overflowX: "auto",
          overflowY: "auto",
          borderRadius: 2,
          border: "1px solid #E2E8F4",
          bgcolor: "#FFFFFF",
          "&::-webkit-scrollbar": { height: 8, width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#D8DEE8",
            borderRadius: 8,
          },
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "auto",
            width: "max-content",
            minWidth: "max-content",
            borderCollapse: "separate",
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map((column, index) => {
                const isFirstColumn = index === 0;
                return (
                  <AppTableHeaderCell
                    key={column.key}
                    minWidth={column.minWidth}
                    txtAlign={column.align ?? "center"}
                    fontColor="#1E2A44"
                    fontWeight={isFirstColumn ? 700 : 600}
                    sx={{
                      ...(isFirstColumn ? STICKY_HEADER_SX : {}),
                      minWidth: column.minWidth,
                      width: column.minWidth,
                      fontSize: "0.74rem",
                      py: 1.1,
                      px: 1.25,
                      whiteSpace: "nowrap",
                      borderBottom: "1px solid #E2E8F4",
                      backgroundColor: "#EEF1F8",
                      "& .MuiTableSortLabel-root": {
                        fontSize: "0.74rem",
                        fontWeight: isFirstColumn ? 700 : 600,
                        whiteSpace: "nowrap",
                      },
                    }}
                  >
                    {renderHeaderLabel(column)}
                  </AppTableHeaderCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const category =
                row.effective_category_id != null
                  ? (categoryMap.get(row.effective_category_id) ?? null)
                  : null;
              const rowBg = getLightCategoryColor(category?.color);

              return (
                <TableRow
                  key={row.customer_id}
                  sx={{
                    "&:last-child td": { borderBottom: 0 },
                    "&:hover td": { filter: "brightness(0.985)" },
                  }}
                >
                  {columns.map((column, index) => {
                    const isFirstColumn = index === 0;
                    return (
                      <AppTableRowCell
                        key={`${row.customer_id}-${column.key}`}
                        textAlign={column.align ?? "center"}
                        align={column.align ?? "center"}
                        minWidth={column.minWidth}
                        fontWeight={
                          isFirstColumn || column.key === "lifetime_value"
                            ? 700
                            : 500
                        }
                        fontSize="0.78rem"
                        color="#1E2A44"
                        sx={{
                          ...(isFirstColumn ? STICKY_CELL_SX : {}),
                          ...CELL_SX,
                          backgroundColor: rowBg,
                          minWidth: column.minWidth,
                          width: column.minWidth,
                          ...(isFirstColumn ? { py: 1 } : {}),
                        }}
                      >
                        {renderCellContent(column, row, category)}
                      </AppTableRowCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <AppTablePagenation
        pageNo={page_no}
        limit={limit}
        totalCount={total_count}
        onPageChange={onPageChange}
        handleRowsChange={onRowsPerPageChange}
      />
    </Box>
  );
};

export default AccountManagementTable;
